/**
 * Geometry of direct manipulation in the calendar grids (Outlook-like): pointer position → time snapped to a step,
 * drag-to-create ranges, moving and resizing events. Pure functions (tested); the grids hold the pointer state.
 */
import { addDays, startOfDay } from './model';

/** Snap step of drag operations (minutes). */
export const SNAP_MINUTES = 15;
/** Length of an event created by a single click (minutes). */
export const CLICK_MINUTES = 30;
/** Pointer movement (px) before a press becomes a drag (a smaller movement is a click). */
export const DRAG_THRESHOLD_PX = 4;
/** Touch: hold this long before dragging, so a normal swipe still scrolls. */
export const LONG_PRESS_MS = 450;

/** Minutes since midnight at a vertical position (0..1 of the day column), snapped down to `step`. */
export function minutesAt(fraction: number, step = SNAP_MINUTES): number {
  const minutes = Math.max(0, Math.min(1, fraction)) * 24 * 60;
  return Math.min(24 * 60 - step, Math.floor(minutes / step) * step);
}

/** Local time on `day` at `minutes` past midnight (DST-safe: hours/minutes set on the local day). */
export function timeOn(day: Date, minutes: number): Date {
  const d = startOfDay(day);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
}

/** Range of a drag on empty grid between two positions of the same day; a click gives CLICK_MINUTES. */
export function createRange(day: Date, fromFraction: number, toFraction: number, isClick: boolean, step = SNAP_MINUTES): { start: Date; end: Date } {
  const a = minutesAt(Math.min(fromFraction, toFraction), step);
  if (isClick) {
    const start = minutesAt(fromFraction, CLICK_MINUTES);
    return { start: timeOn(day, start), end: timeOn(day, start + CLICK_MINUTES) };
  }
  const b = Math.max(a + step, minutesAt(Math.max(fromFraction, toFraction), step) + step);
  return { start: timeOn(day, a), end: b >= 24 * 60 ? addDays(startOfDay(day), 1) : timeOn(day, b) };
}

/** Event moved so its start lands at `minutes` past midnight of `day` (duration kept). */
export function moveTo(event: { start: Date; end: Date }, day: Date, minutes: number): { start: Date; end: Date } {
  const start = timeOn(day, minutes);
  return { start, end: new Date(start.getTime() + (event.end.getTime() - event.start.getTime())) };
}

/** Event moved to another day at the same time of day (month view). */
export function moveToDay(event: { start: Date; end: Date }, day: Date): { start: Date; end: Date } {
  const minutes = event.start.getHours() * 60 + event.start.getMinutes();
  return moveTo(event, day, minutes);
}

/** Resize one edge to `minutes` past midnight of the event's day; at least one step long. */
export function resizeEdge(event: { start: Date; end: Date }, edge: 'start' | 'end', day: Date, minutes: number, step = SNAP_MINUTES): { start: Date; end: Date } {
  if (edge === 'end') {
    const end = minutes + step >= 24 * 60 ? addDays(startOfDay(day), 1) : timeOn(day, minutes + step);
    return { start: event.start, end: end.getTime() - event.start.getTime() < step * 60_000 ? new Date(event.start.getTime() + step * 60_000) : end };
  }
  const start = timeOn(day, minutes);
  return { start: event.end.getTime() - start.getTime() < step * 60_000 ? new Date(event.end.getTime() - step * 60_000) : start, end: event.end };
}

/** Keyboard move/resize by steps (Alt+arrows, Shift+Alt+arrows). */
export function nudge(event: { start: Date; end: Date }, kind: 'move' | 'resize', minutes: number, days = 0): { start: Date; end: Date } {
  if (kind === 'move') {
    const start = new Date(addDays(event.start, days).getTime() + minutes * 60_000);
    return { start, end: new Date(start.getTime() + (event.end.getTime() - event.start.getTime())) };
  }
  const end = new Date(event.end.getTime() + minutes * 60_000);
  return { start: event.start, end: end.getTime() - event.start.getTime() < SNAP_MINUTES * 60_000 ? event.end : end };
}

/** All-day range covering the days from `a` to `b` (either order), end exclusive. */
export function allDayRange(a: Date, b: Date): { start: Date; end: Date } {
  const [first, last] = a <= b ? [a, b] : [b, a];
  return { start: startOfDay(first), end: addDays(startOfDay(last), 1) };
}

/** Same instant? (avoids saving a drag that ended where it started) */
export const sameRange = (a: { start: Date; end: Date }, b: { start: Date; end: Date }) => a.start.getTime() === b.start.getTime() && a.end.getTime() === b.end.getTime();

